// Radial gauge for a single 0–max metric (SoC, SoH, round-trip efficiency,
// performance %). Renders a value arc over a muted track with a centred label.

import { memo } from "react";
import { RadialBar, RadialBarChart, ResponsiveContainer, PolarAngleAxis } from "recharts";

import { ANIMATION_ACTIVE } from "@/components/charts/theme";

export interface RadialGaugeProps {
  value: number;
  max?: number;
  height?: number;
  color?: string;
  label?: string;
  /** Override the centred big text (defaults to `${value}${suffix}`). */
  display?: string;
  suffix?: string;
}

function RadialGaugeBase({
  value,
  max = 100,
  height = 180,
  color = "#E4E4E7",
  label,
  display,
  suffix = "%",
}: RadialGaugeProps) {
  const data = [{ name: label ?? "value", value: Math.max(0, Math.min(value, max)) }];

  return (
    <div className="relative" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%" debounce={60}>
        <RadialBarChart
          innerRadius="72%"
          outerRadius="100%"
          data={data}
          startAngle={220}
          endAngle={-40}
          barSize={12}
        >
          <PolarAngleAxis type="number" domain={[0, max]} angleAxisId={0} tick={false} />
          <RadialBar
            dataKey="value"
            cornerRadius={8}
            background={{ fill: "#1f1f1f" }}
            fill={color}
            isAnimationActive={ANIMATION_ACTIVE}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-2xl font-semibold text-zinc-100">
          {display ?? `${value.toFixed(0)}${suffix}`}
        </div>
        {label && <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>}
      </div>
    </div>
  );
}

export const RadialGauge = memo(RadialGaugeBase);
